"use client";

/**
 * CardBackgroundPreview
 * Single source of truth for rendering a CardBackground value visually.
 * Used by both the canvas cover tile (full-bleed `fill` variant) and the editor row's
 * small icon-sized swatch (`swatch` variant).
 */

import type React from "react";
import { Image as ImageIcon } from "lucide-react";
import { getBackgroundStyle } from "../../_logic/background-styles";
import { isValidStorageUrl } from "../../_logic/utils";
import type { CardBackground } from "../../_types";

type Variant = "fill" | "swatch";

interface CardBackgroundPreviewProps {
  /** The background value to render (defaults to "none" placeholder when undefined) */
  background: CardBackground | undefined;
  /** Page primary color, used for the brand-gradient kind */
  primaryColor: string;
  /** "fill" = absolutely-positioned full-bleed (for the cover tile); "swatch" = inline 20×20 chip (for the row) */
  variant: Variant;
}

const FILL_CLASSES = "absolute inset-0";
const SWATCH_CLASSES = "block h-5 w-5 shrink-0 rounded border border-gray-200";
const SWATCH_PLACEHOLDER_CLASSES =
  "flex h-5 w-5 shrink-0 items-center justify-center rounded border border-dashed border-gray-300 bg-gray-50";

/**
 * Renders a CardBackground visually in one of two variants.
 * @param background - the background value
 * @param primaryColor - page primary color (used by the brand-gradient kind)
 * @param variant - "fill" for the canvas tile, "swatch" for the editor row icon
 */
export function CardBackgroundPreview({
  background,
  primaryColor,
  variant,
}: CardBackgroundPreviewProps): React.ReactElement {
  // Only render image-kind backgrounds when the URL is a valid HTTPS Supabase Storage URL.
  // This prevents CSS/HTML injection via attacker-controlled URLs and enforces HTTPS for
  // content embedded on public landing pages.
  if (background?.kind === "image" && background.url && isValidStorageUrl(background.url)) {
    if (variant === "fill") {
      return (
        <img
          src={background.url}
          alt=""
          loading="lazy"
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover"
          aria-hidden="true"
        />
      );
    }
    return (
      <span className={`${SWATCH_CLASSES} relative overflow-hidden`} aria-hidden="true">
        <img
          src={background.url}
          alt=""
          width={20}
          height={20}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </span>
    );
  }

  if (!background || background.kind === "none" || background.kind === "image") {
    if (variant === "fill") {
      return <div className={`${FILL_CLASSES} bg-gray-300`} aria-hidden="true" />;
    }
    return (
      <span className={SWATCH_PLACEHOLDER_CLASSES}>
        <ImageIcon className="h-3 w-3 text-gray-400" aria-hidden="true" />
      </span>
    );
  }

  const style = getBackgroundStyle(background, primaryColor);
  if (variant === "fill") {
    return <div className={FILL_CLASSES} style={style ?? undefined} aria-hidden="true" />;
  }
  return <span className={SWATCH_CLASSES} style={style ?? undefined} aria-hidden="true" />;
}
